/**
 * Events Hub
 * Location filtering, upcoming/past split, and card <-> map pin sync for
 * sections/events-hub.liquid. Cards are server-rendered; this enhances them.
 */
(function () {
  'use strict';

  var MARKET_LABELS = {
    all: 'All Events',
    arizona: 'Arizona',
    oregon: 'Oregon',
    washington: 'Washington',
    other: 'Other'
  };

  var desktopQuery = window.matchMedia('(min-width: 992px)');

  function todayIso() {
    var d = new Date();
    var m = String(d.getMonth() + 1).padStart(2, '0');
    var day = String(d.getDate()).padStart(2, '0');
    return d.getFullYear() + '-' + m + '-' + day;
  }

  function escapeHtml(str) {
    var div = document.createElement('div');
    div.textContent = str || '';
    return div.innerHTML;
  }

  function EventsHub(root) {
    this.root = root;
    this.upcomingList = root.querySelector('[data-upcoming-list]');
    this.pastList = root.querySelector('[data-past-list]');
    this.emptyState = root.querySelector('[data-events-empty]');
    this.status = root.querySelector('[data-results-status]');
    this.filterButtons = Array.prototype.slice.call(root.querySelectorAll('[data-filter]'));
    this.mapToggle = root.querySelector('[data-map-toggle]');
    this.mapEl = root.querySelector('[data-map]');
    this.markers = {};
    this.showroomMarkers = [];
    this.showShowrooms = false;
    this.activeId = null;
    this.filter = 'all';

    if (!this.upcomingList) return;

    this.moveExpiredToPast();
    this.sortCards();
    this.upcomingCards = Array.prototype.slice.call(this.upcomingList.querySelectorAll('.event-card'));
    this.pastCards = this.pastList ? Array.prototype.slice.call(this.pastList.querySelectorAll('.event-card')) : [];

    this.updateCounts();
    this.bindFilters();
    this.bindCards();
    this.bindMapToggle();
    this.bindShowroomsToggle();
    this.setStickyOffset();

    var params = new URLSearchParams(window.location.search);
    var initial = (params.get('location') || 'all').toLowerCase();
    this.applyFilter(MARKET_LABELS[initial] ? initial : 'all', { silent: true, skipUrl: true });

    this.whenLeafletReady(this.initMap.bind(this));
  }

  /* ---------------------------------------------------------------------- */
  /* Data                                                                    */
  /* ---------------------------------------------------------------------- */

  // The page may be served from cache after an event ends; re-check with the visitor's date.
  EventsHub.prototype.moveExpiredToPast = function () {
    var today = todayIso();
    var cards = this.upcomingList.querySelectorAll('.event-card');
    Array.prototype.forEach.call(cards, function (card) {
      if (card.dataset.end && card.dataset.end < today) {
        if (!this.pastList) {
          card.remove();
          return;
        }
        card.classList.remove('event-card--list');
        card.classList.add('event-card--grid');
        var mapBtn = card.querySelector('[data-event-locate]');
        if (mapBtn) mapBtn.remove();
        this.pastList.prepend(card);
      }
    }, this);
  };

  EventsHub.prototype.sortCards = function () {
    var emptyState = this.emptyState;
    var sortInto = function (list, dir) {
      if (!list) return;
      var cards = Array.prototype.slice.call(list.querySelectorAll('.event-card'));
      cards.sort(function (a, b) {
        var as = a.dataset.start || '';
        var bs = b.dataset.start || '';
        if (as === bs) return 0;
        return (as < bs ? -1 : 1) * dir;
      });
      cards.forEach(function (card) {
        if (emptyState && emptyState.parentNode === list) {
          list.insertBefore(card, emptyState);
        } else {
          list.appendChild(card);
        }
      });
    };
    sortInto(this.upcomingList, 1);
    sortInto(this.pastList, -1);
  };

  EventsHub.prototype.matches = function (card, filter) {
    if (filter === 'all') return true;
    return (' ' + (card.dataset.markets || '') + ' ').indexOf(' ' + filter + ' ') !== -1;
  };

  EventsHub.prototype.updateCounts = function () {
    var self = this;
    this.filterButtons.forEach(function (btn) {
      var key = btn.dataset.filter;
      var count = self.upcomingCards.filter(function (c) { return self.matches(c, key); }).length;
      var el = btn.querySelector('[data-count]');
      if (el) el.textContent = '(' + count + ')';
    });
  };

  /* ---------------------------------------------------------------------- */
  /* Filtering                                                               */
  /* ---------------------------------------------------------------------- */

  EventsHub.prototype.bindFilters = function () {
    var self = this;
    this.filterButtons.forEach(function (btn) {
      btn.addEventListener('click', function () {
        self.applyFilter(btn.dataset.filter);
      });
    });
  };

  EventsHub.prototype.applyFilter = function (filter, opts) {
    opts = opts || {};
    var self = this;
    this.filter = filter;

    this.filterButtons.forEach(function (btn) {
      btn.setAttribute('aria-pressed', btn.dataset.filter === filter ? 'true' : 'false');
    });

    var visible = 0;
    this.upcomingCards.forEach(function (card) {
      var show = self.matches(card, filter);
      card.hidden = !show;
      if (show) visible++;
    });

    var pastVisible = 0;
    this.pastCards.forEach(function (card) {
      var show = self.matches(card, filter);
      card.hidden = !show;
      if (show) pastVisible++;
    });
    var pastCount = this.root.querySelector('[data-count="past"]');
    if (pastCount) pastCount.textContent = '(' + pastVisible + ')';
    var pastEmpty = this.root.querySelector('[data-past-empty]');
    if (pastEmpty) pastEmpty.hidden = pastVisible > 0;

    this.updateEmptyState(visible, filter);

    if (this.activeId) {
      var activeCard = this.cardById(this.activeId);
      if (!activeCard || activeCard.hidden) this.setActive(null);
    }

    this.syncMarkers();

    if (!opts.silent && this.status) {
      var label = filter === 'all' ? 'all locations' : MARKET_LABELS[filter];
      this.status.textContent = visible + ' upcoming event' + (visible === 1 ? '' : 's') + ' in ' + label + '.';
    }

    if (!opts.skipUrl && window.history && window.history.replaceState) {
      var url = new URL(window.location.href);
      if (filter === 'all') {
        url.searchParams.delete('location');
      } else {
        url.searchParams.set('location', filter);
      }
      window.history.replaceState({}, '', url);
    }
  };

  EventsHub.prototype.updateEmptyState = function (visible, filter) {
    if (!this.emptyState) return;
    this.emptyState.hidden = visible > 0;
    if (visible > 0) return;

    var heading = this.emptyState.querySelector('.event-empty__heading');
    if (heading) {
      if (!heading.dataset.original) heading.dataset.original = heading.textContent;
      heading.textContent = filter === 'all'
        ? heading.dataset.original
        : 'No upcoming ' + MARKET_LABELS[filter] + ' events—yet.';
    }

    var btn = this.emptyState.querySelector('.btn');
    if (btn) {
      btn.hidden = filter === 'all';
      if (!btn.dataset.bound) {
        btn.dataset.bound = 'true';
        btn.addEventListener('click', function (e) {
          e.preventDefault();
          this.applyFilter('all');
        }.bind(this));
      }
    }
  };

  /* ---------------------------------------------------------------------- */
  /* Cards                                                                   */
  /* ---------------------------------------------------------------------- */

  EventsHub.prototype.cardById = function (id) {
    return this.upcomingList.querySelector('.event-card[data-event-id="' + id + '"]');
  };

  EventsHub.prototype.bindCards = function () {
    var self = this;
    this.upcomingCards.forEach(function (card) {
      var id = card.dataset.eventId;

      card.addEventListener('mouseenter', function () { self.highlightMarker(id, true); });
      card.addEventListener('mouseleave', function () {
        if (self.activeId !== id) self.highlightMarker(id, false);
      });
      card.addEventListener('focusin', function () { self.highlightMarker(id, true); });
      card.addEventListener('focusout', function (e) {
        if (!card.contains(e.relatedTarget) && self.activeId !== id) self.highlightMarker(id, false);
      });

      // Clicking the card body (not a link) selects it and centers its pin.
      card.addEventListener('click', function (e) {
        if (e.target.closest('a')) return;
        var locateBtn = e.target.closest('[data-event-locate]');
        if (locateBtn && !desktopQuery.matches) self.openMap();
        self.select(id, { pan: true, openPopup: !!locateBtn });
      });
    });
  };

  EventsHub.prototype.setActive = function (id) {
    var prev = this.activeId;
    if (prev && prev !== id) {
      var prevCard = this.cardById(prev);
      if (prevCard) prevCard.classList.remove('is-active');
      this.highlightMarker(prev, false);
    }
    this.activeId = id;
    if (id) {
      var card = this.cardById(id);
      if (card) card.classList.add('is-active');
      this.highlightMarker(id, true);
    }
  };

  EventsHub.prototype.select = function (id, opts) {
    opts = opts || {};
    this.setActive(id);

    var marker = this.markers[id];
    if (marker && this.map) {
      if (opts.pan) {
        this.map.flyTo(marker.getLatLng(), Math.max(this.map.getZoom(), 8), { duration: 0.6 });
      }
      if (opts.openPopup) marker.openPopup();
    }

    if (opts.scrollToCard) {
      var card = this.cardById(id);
      if (card) {
        var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        card.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'nearest' });
        if (!card.hasAttribute('tabindex')) card.setAttribute('tabindex', '-1');
        card.focus({ preventScroll: true });
      }
    }
  };

  /* ---------------------------------------------------------------------- */
  /* Map                                                                     */
  /* ---------------------------------------------------------------------- */

  EventsHub.prototype.whenLeafletReady = function (cb) {
    if (window.L) return cb();
    var tries = 0;
    var timer = setInterval(function () {
      tries++;
      if (window.L) {
        clearInterval(timer);
        cb();
      } else if (tries > 100) {
        clearInterval(timer);
      }
    }, 100);
  };

  EventsHub.prototype.initMap = function () {
    if (!this.mapEl || this.map) return;
    var self = this;
    var L = window.L;

    this.map = L.map(this.mapEl, {
      scrollWheelZoom: false, // never hijack page scrolling
      zoomControl: true
    });

    L.tileLayer(this.root.dataset.tileUrl || 'https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 18,
      attribution: this.root.dataset.tileAttribution || '&copy; OpenStreetMap contributors'
    }).addTo(this.map);

    // Let wheel zoom work only after the visitor deliberately clicks into the map.
    this.map.on('click', function () { self.map.scrollWheelZoom.enable(); });
    this.map.on('mouseout', function () { self.map.scrollWheelZoom.disable(); });

    this.addShowroomMarkers();
    this.addEventMarkers();
    this.syncMarkers();
  };

  EventsHub.prototype.addShowroomMarkers = function () {
    var L = window.L;
    var self = this;
    var dataEl = this.root.querySelector('[data-showrooms]');
    var showrooms = [];
    try {
      showrooms = JSON.parse(dataEl ? dataEl.textContent : '[]');
    } catch (e) {
      showrooms = [];
    }

    showrooms.forEach(function (s) {
      var icon = L.divIcon({
        className: 'ehub-marker ehub-marker--showroom',
        html: '<span class="ehub-pin ehub-pin--showroom"><span>M</span></span>',
        iconSize: [26, 26],
        iconAnchor: [13, 13],
        popupAnchor: [0, -14]
      });
      var marker = L.marker([s.lat, s.lng], {
        icon: icon,
        title: s.name + ' (showroom)',
        alt: s.name + ' showroom',
        keyboard: true,
        riseOnHover: true
      });
      var html = '<span class="ehub-popup__date">MULE Showroom</span>' +
        '<strong class="ehub-popup__title">' + escapeHtml(s.name) + '</strong>' +
        (s.address ? escapeHtml(s.address).replace(/\n/g, '<br>') + '<br>' : '') +
        (s.url ? '<a href="' + escapeHtml(s.url) + '">Visit location page &rarr;</a>' : '');
      marker.bindPopup(html, { className: 'ehub-popup' });
      marker._market = s.market;
      self.showroomMarkers.push(marker);
    });
  };

  EventsHub.prototype.addEventMarkers = function () {
    var L = window.L;
    var self = this;

    this.upcomingCards.forEach(function (card) {
      var lat = parseFloat(card.dataset.lat);
      var lng = parseFloat(card.dataset.lng);
      if (isNaN(lat) || isNaN(lng)) return;

      var id = card.dataset.eventId;
      var icon = L.divIcon({
        className: 'ehub-marker ehub-marker--event',
        html: '<span class="ehub-pin ehub-pin--event"></span>',
        iconSize: [28, 28],
        iconAnchor: [14, 28],
        popupAnchor: [0, -30]
      });
      var marker = L.marker([lat, lng], {
        icon: icon,
        title: card.dataset.title,
        alt: card.dataset.title,
        keyboard: true,
        zIndexOffset: 500
      });

      var html = '<span class="ehub-popup__date">' + escapeHtml(card.dataset.dateLabel) + '</span>' +
        '<strong class="ehub-popup__title">' + escapeHtml(card.dataset.title) + '</strong>' +
        (card.dataset.place ? escapeHtml(card.dataset.place) + '<br>' : '') +
        '<a href="' + escapeHtml(card.dataset.url) + '">Event details &rarr;</a>';
      marker.bindPopup(html, { className: 'ehub-popup' });

      marker.on('click', function () {
        self.select(id, { scrollToCard: true });
      });
      marker.on('mouseover', function () { self.highlightCard(id, true); });
      marker.on('mouseout', function () { self.highlightCard(id, false); });

      self.markers[id] = marker;
    });
  };

  EventsHub.prototype.highlightMarker = function (id, on) {
    var marker = this.markers[id];
    if (!marker) return;
    var el = marker.getElement();
    if (el) el.classList.toggle('is-active', on);
    marker.setZIndexOffset(on ? 2000 : 500);
  };

  EventsHub.prototype.highlightCard = function (id, on) {
    if (this.activeId === id) return;
    var card = this.cardById(id);
    if (card) card.classList.toggle('is-active', on);
  };

  EventsHub.prototype.syncMarkers = function () {
    if (!this.map) return;
    var self = this;
    var bounds = [];

    Object.keys(this.markers).forEach(function (id) {
      var marker = self.markers[id];
      var card = self.cardById(id);
      if (card && !card.hidden) {
        marker.addTo(self.map);
        bounds.push(marker.getLatLng());
      } else {
        marker.remove();
      }
    });

    // Showroom pins are hidden until the visitor turns them on.
    var showroomBounds = [];
    this.showroomMarkers.forEach(function (marker) {
      var inMarket = self.filter === 'all' || marker._market === self.filter;
      if (inMarket) showroomBounds.push(marker.getLatLng());
      if (inMarket && self.showShowrooms) {
        marker.addTo(self.map);
        bounds.push(marker.getLatLng());
      } else {
        marker.remove();
      }
    });

    if (this.activeId) this.highlightMarker(this.activeId, true);
    // With no event pins to show, still frame the selected market.
    this.fitTo(bounds.length ? bounds : showroomBounds);
  };

  EventsHub.prototype.fitTo = function (latlngs) {
    if (!this.map) return;
    if (!latlngs.length) {
      this.map.setView([42, -118], 4); // Western US
      return;
    }
    if (latlngs.length === 1) {
      this.map.setView(latlngs[0], 9);
      return;
    }
    this.map.fitBounds(window.L.latLngBounds(latlngs), { padding: [40, 40], maxZoom: 10 });
  };

  /* ---------------------------------------------------------------------- */
  /* Layout                                                                  */
  /* ---------------------------------------------------------------------- */

  EventsHub.prototype.bindMapToggle = function () {
    if (!this.mapToggle) return;
    var self = this;
    this.mapToggle.hidden = false;
    this.mapToggle.addEventListener('click', function () {
      if (self.root.classList.contains('is-map-open')) {
        self.closeMap();
      } else {
        self.openMap();
      }
    });
  };

  EventsHub.prototype.bindShowroomsToggle = function () {
    var btn = this.root.querySelector('[data-showrooms-toggle]');
    if (!btn) return;
    var self = this;
    var label = btn.querySelector('[data-showrooms-toggle-label]');
    btn.addEventListener('click', function () {
      self.showShowrooms = !self.showShowrooms;
      btn.setAttribute('aria-pressed', self.showShowrooms ? 'true' : 'false');
      if (label) label.textContent = self.showShowrooms ? 'Hide MULE Showrooms' : 'View MULE Showrooms';
      if (!self.showShowrooms) self.map && self.map.closePopup();
      self.syncMarkers();
    });
  };

  EventsHub.prototype.openMap = function () {
    this.root.classList.add('is-map-open');
    if (this.mapToggle) {
      this.mapToggle.setAttribute('aria-expanded', 'true');
      var label = this.mapToggle.querySelector('[data-map-toggle-label]');
      if (label) label.textContent = 'Hide map';
    }
    this.refreshMapSize();
  };

  EventsHub.prototype.closeMap = function () {
    this.root.classList.remove('is-map-open');
    if (this.mapToggle) {
      this.mapToggle.setAttribute('aria-expanded', 'false');
      var label = this.mapToggle.querySelector('[data-map-toggle-label]');
      if (label) label.textContent = 'View map';
    }
  };

  // Leaflet measures its container on init; re-measure after it becomes visible.
  EventsHub.prototype.refreshMapSize = function () {
    var self = this;
    if (!this.map) return;
    requestAnimationFrame(function () {
      self.map.invalidateSize();
      self.syncMarkers();
      if (self.activeId && self.markers[self.activeId]) {
        self.map.setView(self.markers[self.activeId].getLatLng(), Math.max(self.map.getZoom(), 8));
      }
    });
  };

  // Keep the sticky map below the sticky header.
  EventsHub.prototype.setStickyOffset = function () {
    var self = this;
    var update = function () {
      var header = document.querySelector('header-container');
      var h = header ? header.getBoundingClientRect().height : 0;
      self.root.style.setProperty('--ehub-sticky-top', Math.round(h + 16) + 'px');
    };
    update();
    window.addEventListener('resize', update);
    desktopQuery.addEventListener('change', function () {
      update();
      self.refreshMapSize();
    });
  };

  function init() {
    document.querySelectorAll('[data-events-hub]').forEach(function (root) {
      if (!root._eventsHub) root._eventsHub = new EventsHub(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Theme editor: re-init when the section is re-rendered.
  document.addEventListener('shopify:section:load', init);
})();
